import React, { useEffect, useRef, useState } from "react";
import TooltipComponent from "../TooltipComponent";
import { TranslationService } from "../../../services/TranslationService";
import { ToastService } from "../bootstrap/Toast";
import FilePortalService, {
  MultiuploadResultDataType
} from "../../../services/FilePortalService";
import FileService, {
  MultiuploadResultType
} from "../../../services/FileService";
import InvoiceService from "../../../services/InvoiceService";
import { InvoiceSetFileResponse } from "../../../entities/invoice/InvoiceSetFileResponse";
import { DomEventService } from "../../../services/DomEventService";
import TaskService from "../../../services/TaskSerivce";
import { TaskSetFileResponse } from "../../task/entities/TaskSetFileResponse";

export type PublicFile = { id: string; name: string };

type PortalTokenType = { token?: string };

type MultiFileUploadProps = {
  files?: PublicFile[];
  parent?: { parentId: number; parentType: "INVOICE" | "TASK" };
  parentAllowEdit?: boolean;
  variant?: boolean;
  token?: string;
  downloadFile?: ({
    fileId,
    filename
  }: {
    fileId: string;
    filename: string;
  }) => void;
  onFilesChange?: (fileIdList: { id: string; name: string }[]) => void;
  onFilesDelete?: (id: string) => void;
};

const MultiFileUploadNew: React.FC<MultiFileUploadProps & PortalTokenType> = ({
  files: filesParams,
  parent,
  parentAllowEdit = true,
  variant = false,
  token = "",
  downloadFile,
  onFilesChange,
  onFilesDelete
}) => {
  const [showDrag, setShowDrag] = useState(false);
  const [files, setFiles] = useState<
    {
      id: string;
      name: string;
    }[]
  >(filesParams || []);
  const [loading, setLoading] = useState(false);

  const dropRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const canEdit = parentAllowEdit;

  const onFileChange = async (newFiles: File[]) => {
    const sizeDeclined = [...newFiles].some((file) => file.size > 50000000);
    if (sizeDeclined) {
      ToastService.showToast(
        TranslationService.translate.FileSizeError,
        undefined,
        "danger"
      );
      return;
    }
    setLoading(true);
    const resultTempFiles = variant
      ? await FilePortalService.multiupload(newFiles, token)
      : await FileService.multiupload(newFiles);
    if (resultTempFiles instanceof Error) {
      ToastService.showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      setLoading(false);
      return false;
    } else {
      // esto me retorna los temporales
      const filesData = variant
        ? (resultTempFiles as MultiuploadResultDataType)?.data?.files.map(
            (x) => ({ id: x.id, name: x.filename })
          )
        : (resultTempFiles as MultiuploadResultType)?.files?.map((x) => ({
            id: x.id,
            name: x.filename
          }));

      // Se guardan los temporales y devuelven el id
      let filesPromises = [];
      let updatedFiles = [];

      if (parent) {
        switch (parent.parentType) {
          case "INVOICE":
            filesPromises = await Promise.all(
              filesData.map((f) =>
                InvoiceService.setFile(parent.parentId.toString(), f.id)
              )
            );
            updatedFiles = [
              ...files,
              ...filesPromises?.map((finalId: InvoiceSetFileResponse, i) => ({
                id: finalId.id.toString(),
                name: filesData[i].name
              }))
            ];
            break;
          case "TASK":
            filesPromises = await Promise.all(
              filesData.map((f) =>
                TaskService.setFile(parent.parentId.toString(), f.id)
              )
            );
            updatedFiles = [
              ...files,
              ...filesPromises?.map((finalId: TaskSetFileResponse, i) => ({
                id: finalId.id.toString(),
                name: filesData[i].name
              }))
            ];
            break;
          default:
            break;
        }
      } else {
        // si setFile devuelve el id definitivo:
        updatedFiles = [...files, ...filesData];
      }

      setFiles(updatedFiles);
      onFilesChange && onFilesChange(updatedFiles);
      setLoading(false);
    }
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = event.target.files ? Array.from(event.target.files) : [];
    if (!newFiles.length) {
      return;
    }
    onFileChange([...newFiles]);
    event.target.value = "";
  };

  const fileDelete = async (fileId: string) => {
    setLoading(true);
    setFiles([...files.filter((el) => el.id !== fileId)]);
    onFilesDelete && onFilesDelete(fileId);
    onFilesChange && onFilesChange([...files.filter((el) => el.id !== fileId)]);
    setLoading(false);
  };

  const handleDownload = async (file: { id: string; name: string }) => {
    if (downloadFile) {
      await downloadFile({ fileId: file.id, filename: file.name });
    }
  };

  const dragLeaveTimeout = useRef<number>(0);

  useEffect(() => {
    const subscriptions = [
      DomEventService.GetBodyEvents("dragover").subscribe((x) => {
        if (x.dataTransfer?.types.includes("Files")) {
          x.preventDefault();
          clearTimeout(dragLeaveTimeout.current);
          setShowDrag(true);
        }
      }),
      DomEventService.GetBodyEvents("dragleave").subscribe((x) => {
        if (x.dataTransfer?.types.includes("Files")) {
          clearTimeout(dragLeaveTimeout.current);
          dragLeaveTimeout.current = setTimeout(
            () => setShowDrag(false),
            100,
            undefined
          );
        }
      }),
      DomEventService.GetBodyEvents("drop").subscribe((x) => {
        if (x.dataTransfer?.types.includes("Files")) {
          if (x.target === dropRef.current) {
            x.preventDefault();
            const files = [...x.dataTransfer.items]
              .map((item) =>
                item.kind === "file" ? item.getAsFile() : undefined
              )
              .filterFalsey();
            if (files.length === 0) {
              files.push(...x.dataTransfer.files);
            }
            onFileChange(files);
          }
          clearTimeout(dragLeaveTimeout.current);
          setShowDrag(false);
        }
      })
    ];
    return () => subscriptions.forEach((x) => x.unsubscribe());
  });

  useEffect(() => {
    setFiles(filesParams || []);
  }, [filesParams]);

  if (!canEdit) {
    return <></>;
  }

  return (
    <div>
      {showDrag ? (
        <div ref={dropRef} className="drop-files-box mb-2">
          {TranslationService.translate.DropYourFilesHere}
        </div>
      ) : (
        <>
          <input
            id="multi-upload-input"
            ref={inputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            className="text-placeholder"
            onChange={handleChange}
          />
          <div className="d-flex align-items-center removeOnPrint">
            <label
              htmlFor="multi-upload-input"
              style={{ width: 45, textAlign: "center" }}
            >
              <TooltipComponent
                title={TranslationService.translate.SelectOrDragAFile}
              >
                <i className="far fa-fw fa-paperclip activity-icon-color my-2 pointer" />
              </TooltipComponent>
            </label>
            <div
              className="files-container col mb-1 gap-1"
              onClick={() => inputRef.current?.click()}
            >
              {files && files?.length > 0 ? (
                files.map((file) => (
                  <div key={file.id} className="file-chip p-0 m-0">
                    {/* Descargar (label/nombre) */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDownload(file);
                      }}
                      className="btn btn-link p-0 m-0 text-start file-name-btn"
                      style={{
                        backgroundColor: "rgba(0, 118, 182, 0.08)",
                        borderRadius: 2
                      }}
                      title={file.name}
                    >
                      <small className="text-secondary text-secondary-label mx-2">
                        {file.name}
                      </small>
                    </button>

                    {/* Eliminar (cruz) */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        fileDelete(file.id);
                      }}
                      className="bg-del-file btn btn-link p-0 m-0"
                      style={{ paddingLeft: 4, paddingRight: 4, zIndex: 9 }}
                      aria-label={TranslationService.translate.Delete}
                      title={TranslationService.translate.Delete}
                    >
                      <svg
                        height="14"
                        width="14"
                        viewBox="0 0 20 20"
                        aria-hidden="true"
                      >
                        <path
                          d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"
                          fill="#0076b6"
                        />
                      </svg>
                    </button>
                  </div>
                ))
              ) : (
                <>{TranslationService.translate.SelectOrDragAFile}</>
              )}
              {loading && <i className="fas fa-spinner-third fa-spin ms-2" />}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
export default MultiFileUploadNew;
